import { Injectable } from '@angular/core';
import { iUserMetadata, iRecreateToken, iTokenResponse, iUserData } from './user.interface';
import { HttpService } from '../http.service';
import { DialogService } from '../dialog.service';
import { iUser } from 'src/app/interfaces/user';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { JwtService } from '../jwt.service';
import { SudoModeService } from '../sudo-mode.service';
import { SignalrService } from '../signalr.service';

declare const ga: any;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private users: { [id: string]: iUserMetadata } = {};
  public user: string;
  public userData: iUserData;

  public promise: Promise<true> = null;

  private latestLocalStorage = { session: '', local: '' };

  constructor(
    private http: HttpService,
    private dialog: DialogService,
    private router: Router,
    private jwtService: JwtService,
    private sudo: SudoModeService,
    private signalr: SignalrService
  ) {
    this.promise = this.initUserList();
  }

  public async initUserList(): Promise<true> {
    this.user = null;
    this.userData = null;

    await this.getUsersFromBrowser();
    await this.signIn();

    setInterval(() => this.lookForChanges(), 2000);

    return true;
  }

  private lookForChanges() {
    if (
      this.latestLocalStorage.session !== localStorage.getItem('sessionUsers') ||
      this.latestLocalStorage.local !== localStorage.getItem('users')
    )
      this.getUsersFromBrowser();
  }

  private async getUsersFromBrowser() {
    const session = (await this.getUsersFromSessionStorage().catch<string[]>(() => [])) || [];
    const local = (await this.getUsersFromLocalStorage().catch<string[]>(() => [])) || [];

    await Promise.all(session.map(k => this.decodeJWT(k, 'renew')));
    await Promise.all(local.map(k => this.decodeJWT(k, 'recreate')));
  }

  private async getUsersFromSessionStorage(): Promise<string[]> {
    const userRenewTokensString = localStorage.getItem('sessionUsers');
    if (!userRenewTokensString || !userRenewTokensString.length) return;

    this.latestLocalStorage.session = userRenewTokensString;

    const userRenewTokens = await this.jwtService
      .parseJSON<string[]>(userRenewTokensString)
      .catch(e => console.error(e));
    if (!userRenewTokens || !userRenewTokens.length) return;
    return userRenewTokens;
  }

  private async getUsersFromLocalStorage(): Promise<string[]> {
    const userRecreateTokensString = localStorage.getItem('users');
    if (!userRecreateTokensString || !userRecreateTokensString.length) return;

    this.latestLocalStorage.local = userRecreateTokensString;

    const userRecreateTokens = await this.jwtService
      .parseJSON<string[]>(userRecreateTokensString)
      .catch(e => console.error(e));
    if (!userRecreateTokens || !userRecreateTokens.length) return;
    return userRecreateTokens;
  }

  private async decodeJWT(jwt: string, type: 'renew' | 'recreate') {
    let token: Promise<number>;
    if (type == 'recreate') token = this.jwtService.addJWT(jwt);
    else token = this.jwtService.addJWT(jwt, { num: 25, type: '%' });

    const tokenNumber = await token.catch(e => console.error(e));
    if (!tokenNumber && tokenNumber !== 0) return;

    this.appendToUsers(tokenNumber, type);
  }

  private appendToUsers(jwt: number, type: 'renew' | 'recreate') {
    const jwtObject = this.jwtService.getJWT<iUser | iRecreateToken>(jwt);

    if (!this.users[jwtObject.parsed.userid]) this.users[jwtObject.parsed.userid] = { activeRenew: false };
    const user = this.users[jwtObject.parsed.userid];

    const tokenKey: 'recreateJWT' | 'userJWT' = type === 'recreate' ? 'recreateJWT' : 'userJWT';

    // If token created before the stored token and the stored token valid
    // or being actively renewed, there is no need to update the token.
    if (
      !!user.activeRenew ||
      (user[tokenKey] &&
        user[tokenKey].jwt &&
        user[tokenKey].jwt.valid() &&
        user[tokenKey].jwt.parsed.iat >= jwtObject.parsed.iat)
    )
      return;

    this.removeUserJwt(jwtObject.parsed.userid, type, true, false);

    if (!user[tokenKey]) user[tokenKey] = {} as any;

    user[tokenKey].id = jwt;
    user[tokenKey].jwt = jwtObject as any;
    user[tokenKey].subscription = null;

    if (type == 'renew') {
      user.userJWT.subscription = jwtObject.expire.subscribe(() => this.renewToken(jwtObject.parsed.userid));

      if (jwtObject.parsed.userid == this.user) this.http.setJWT(jwtObject, () => this.signIn());
    }

    this.clearUsers();
  }

  // Return true if token was really renewed, otherwise false;
  private async renewToken(userId: string, force: boolean = false): Promise<boolean> {
    const user = this.users[userId];
    if (
      !user ||
      (!user.userJWT && !user.recreateJWT) ||
      (user.activeRenew && (await user.activeRenew.then(() => true).catch(() => true)))
    )
      return false;

    // No need to renew user that can be recreated
    if (!user.recreateJWT || force) {
      // Must renew logged in user
      if (userId != this.user && user.userJWT && user.userJWT.jwt.valid()) return false;

      // Create a promise and store it's resolver
      // To pass for active renew, so it won't
      // return immediately before user is renewed.
      let promiseResolver: () => void;
      user.activeRenew = new Promise<void>(resolve => (promiseResolver = resolve));
      const res = await this.renewTokenHttp(user).catch(e => this.decodeHttpError(e, userId));
      // Resolve promise to let other renewToken go
      promiseResolver();
      user.activeRenew = false;

      if (res && 'authError' in res) {
        this.removeUserJwt(userId, 'both');
        return false;
      } else if (!res || !res['User_JWT']) return false;

      await this.decodeJWT((res as iTokenResponse).User_JWT, 'renew');
      if ((res as iTokenResponse).Recreate_JWT) await this.decodeJWT((res as iTokenResponse).Recreate_JWT, 'recreate');

      return true;
    }
    this.removeUserJwt(userId, 'renew', userId === this.user);
    return false;
  }

  private async renewTokenHttp(user: iUserMetadata): Promise<iTokenResponse | void> {
    let http: Promise<iTokenResponse | void>;
    if (user.userJWT)
      http = this.http
        .post<iTokenResponse>('Authentication', 'token/renew', {}, user.userJWT.jwt.jwt)
        .catch<iTokenResponse>((e: HttpErrorResponse) => {
          if ((e.status == 403 || e.status == 401) && user.recreateJWT)
            return this.http.post<iTokenResponse>('Authentication', 'token/recreate', {}, user.recreateJWT.jwt.jwt);
          else return Promise.reject(e);
        });
    else if (user.recreateJWT)
      http = this.http.post<iTokenResponse>('Authentication', 'token/recreate', {}, user.recreateJWT.jwt.jwt);
    else http = Promise.reject();

    return http;
  }

  private decodeHttpError(e: HttpErrorResponse, userId: string) {
    const error = this.http.decodeError(e, {
      0: 'המערכת לא הצליחה לחבר אותך עקב בעיה בחיבור לאינטרנט. בדוק את החיבור ונסה שנית על ידי ריענון העמוד',
      400: 'המערכת נכשלה בניסיון לחבר אותך. אם השגיאה חוזרת על עצמה, פנה למתכנת.',
      401: 'תוקף החיבור שלך פג. עליך להתחבר מחדש.',
      EXPIRED_TOKEN: 'תוקף החיבור פג. יש להתחבר שנית.'
    });

    const authError = error.status == 401 || error.status == 403;
    if (authError && this.user == userId)
      this.dialog
        .alert('משתמש זה מנותק מהמערכת', undefined, 'רענון', dialogRef => (dialogRef.disableClose = true))
        .then(() => window.location.reload());
    else
      this.dialog
        .alert('שגיאת התחברות', error.msg, 'רענון', dialogRef => (dialogRef.disableClose = true))
        .then(() => window.location.reload());

    return { authError };
  }

  private removeUserJwt(
    userId: string,
    type: 'recreate' | 'renew' | 'both',
    removingBeforeReplacement: boolean = false,
    callClearUsers: boolean = true
  ) {
    const user = this.users[userId];
    if (!user) return;

    if (type == 'recreate' || type == 'both') user.recreateJWT = null;
    if (type == 'renew' || type == 'both') {
      if (userId == this.user && !removingBeforeReplacement) {
        this.user = null;
        this.userData = null;
        this.http.removeJWT();
      }
      if (user.userJWT && user.userJWT.subscription) user.userJWT.subscription.unsubscribe();
      user.userJWT = null;
    }

    if (callClearUsers) this.clearUsers();
  }

  private clearUsers() {
    Object.entries(this.users).forEach(([userId, user]) => {
      if (!user.userJWT || user.userJWT.jwt.expired())
        this.removeUserJwt(userId, 'renew', userId === this.user && !!user.recreateJWT, false);

      if (!user.userJWT && !user.recreateJWT) {
        delete this.users[userId];
      }
    });

    this.saveUsersToBrowser();
  }

  private saveUsersToBrowser() {
    const session: string[] = [];
    const local: string[] = [];

    Object.entries(this.users).forEach(([, user]) => {
      if (user.userJWT) session.push(user.userJWT.jwt.jwt);
      if (user.recreateJWT) local.push(user.recreateJWT.jwt.jwt);
    });

    localStorage.setItem('sessionUsers', JSON.stringify(session));
    localStorage.setItem('users', JSON.stringify(local));
  }

  public getList(): iUserMetadata[] {
    this.clearUsers();

    return Object.values(this.users);
  }

  public async signIn(userid: string = this.user || localStorage.getItem('loggedin')): Promise<boolean> {
    if (!userid || !this.users[userid]) return false;

    this.sudo.remove();

    this.user = userid;
    const reallyRenewed = await this.renewToken(this.user, true);

    // If failed to login, should remove user from being loggedin
    // Can't use reallyRenewed, because it can return false
    // even when user was loggedin correctly, but other renew
    // proccess managed it.
    if (this.user != userid || !this.users[userid].userJWT) {
      this.user = null;
      return false;
    }

    localStorage.setItem('loggedin', userid);
    this.http.setJWT(this.users[userid].userJWT ? this.users[userid].userJWT.jwt : null, () => this.signIn());
    this.getUserData();

    // Set user in google analytics
    ga('set', 'userId', userid);

    if (reallyRenewed === true) this.signalr.createConnection();

    return true;
  }

  private getUserData() {
    const user = this.users[this.user].userJWT.jwt.parsed;

    this.userData = {
      userid: this.user,
      title: user.title,
      email: user.email,
      phone: user.phone,
      name: user.name,
      department: user.department,
      telegramToken: user.telegramToken,
      authorization: user.authorization
    };
  }

  public async addUser(user_token: string, recreate_token?: string): Promise<void> {
    if (recreate_token) await this.decodeJWT(recreate_token, 'recreate');
    await this.decodeJWT(user_token, 'renew');
  }

  public async signOut(): Promise<void> {
    if (!this.user) return;

    if (!(await this.dialog.confirm('האם אתה בטוח שברצונך להתנתק?', null, 'כן', 'לא'))) return;

    const user = this.users[this.user];

    await this.http
      .post(
        'Authentication',
        'token/remove',
        [...(user.recreateJWT ? [user.recreateJWT.jwt.jwt] : []), ...(user.userJWT ? [user.userJWT.jwt.jwt] : [])],
        true
      )
      .catch(() => {});

    this.removeUserJwt(this.user, 'both');

    this.signalr.logout();

    this.router.navigate(['/']);

    this.dialog.alert(
      'התנתקת בהצלחה',
      'שים לב כי התנתקת מהמשתמש אליו היית מחובר. אם במחשב זה ישנם משתמשים נוספים מחוברים, יש להתנתק מהם בנפרד.'
    );
  }
}
